import { SectionTitle } from "@/components/Title";
import { SizingWrapper } from "@/components/Wrapper";
import {
  AlertTitle,
  Box,
  Checkbox,
  Chip,
  Divider,
  Grid,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import Tooltip from "@mui/material/Tooltip";
import HelpIcon from "@mui/icons-material/Help";
import { LoadingButton } from "@mui/lab";
import React from "react";
import type { SearchSectionProps } from "../types";
import { CheckboxGroup } from "@/components/Checkbox";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { Label } from "@/components/Label";
import {
  preventNonNumericValueWhenKeyDown,
  preventNonNumericValueWhenPaste,
} from "@/utils/utils";

/**
 * 空文字、または min 以上の整数の場合に true を返すバリデーション関数を生成する。
 * Number() は "1.5" のような小数も通してしまうため、整数（数字のみ）であることを併せて検証する。
 */
const isBlankOrIntegerAtLeast =
  (min: number) =>
  (val: string | undefined): boolean => {
    if (!val || val.length === 0) return true;
    return /^\d+$/.test(val) && Number(val) >= min;
  };

export const searchConditionSchema = z
  .object({
    prefectures: z.array(z.string()),
    location: z.string(),
    pictureTypeZenbujiko: z.boolean().default(true),
    pictureTypeShoyushajiko: z.boolean().default(true),
    isEmptyHouse: z.boolean(),
    ownerName: z.string().trim(),
    ownerNameSelectType: z
      .enum(["startswith", "endswith", "contains", "exact"])
      .optional(),
    ownerAddress: z.string().optional(),
    isSoleOwnership: z.boolean().default(false),
    isCurrentOwnerOnly: z.boolean().default(true),
    isLatestOwnerOnly: z.boolean().default(false),
    mochibunFrom: z
      .string()
      .refine(isBlankOrIntegerAtLeast(2), "2以上の整数を入力してください"),
    mochibunTo: z
      .string()
      .refine(isBlankOrIntegerAtLeast(2), "2以上の整数を入力してください"),
    ownerPropertyCountFrom: z
      .string()
      .optional()
      .refine(isBlankOrIntegerAtLeast(1), "1以上の整数を入力してください"),
    ownerPropertyCountTo: z
      .string()
      .optional()
      .refine(isBlankOrIntegerAtLeast(1), "1以上の整数を入力してください"),
  })
  .refine(
    (args) => {
      const { mochibunFrom, mochibunTo } = args;
      if (mochibunFrom.length !== 0 && mochibunTo.length !== 0) {
        return parseInt(mochibunFrom) <= parseInt(mochibunTo);
      }
      return true;
    },
    {
      message: "「以上」は「以下」より小さくしてください",
      path: ["mochibunTo"],
    }
  )
  .refine(
    (args) => {
      const { ownerPropertyCountFrom, ownerPropertyCountTo } = args;
      if (
        ownerPropertyCountFrom &&
        ownerPropertyCountFrom.length !== 0 &&
        ownerPropertyCountTo &&
        ownerPropertyCountTo.length !== 0
      ) {
        return (
          parseInt(ownerPropertyCountFrom) <= parseInt(ownerPropertyCountTo)
        );
      }
      return true;
    },
    {
      message: "「以上」は「以下」より小さくしてください",
      path: ["ownerPropertyCountTo"],
    }
  )
  .refine(
    (args) => {
      const { pictureTypeZenbujiko, pictureTypeShoyushajiko } = args;
      return pictureTypeZenbujiko || pictureTypeShoyushajiko;
    },
    {
      message: "事項種別を少なくとも1つ選択してください",
      path: ["pictureTypeShoyushajiko"],
    }
  );

export type SearchConditionFormSchema = z.infer<typeof searchConditionSchema>;

const alertTitleSx = { fontWeight: "bold", color: "red", fontSize: "0.85rem" };

export const SearchSection: React.FC<SearchSectionProps> = ({
  handleSearch,
  prefectures,
  isLoading,
  initialOwnerName,
}) => {
  const {
    control,
    getValues,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    trigger,
  } = useForm<SearchConditionFormSchema>({
    mode: "all",
    defaultValues: {
      prefectures: [],
      location: "",
      pictureTypeZenbujiko: true,
      pictureTypeShoyushajiko: true,
      isEmptyHouse: false,
      ownerName: initialOwnerName ?? "",
      ownerNameSelectType: initialOwnerName ? "startswith" : undefined,
      ownerAddress: "",
      isSoleOwnership: false,
      isCurrentOwnerOnly: true,
      isLatestOwnerOnly: false,
      mochibunFrom: "",
      mochibunTo: "",
      ownerPropertyCountFrom: "",
      ownerPropertyCountTo: "",
    },
    resolver: zodResolver(searchConditionSchema),
  });

  // react-hook-formのwatchを使用してフォームの値を監視
  const isEmptyHouse = watch("isEmptyHouse");
  const isSoleOwnership = watch("isSoleOwnership");
  const mochibunFrom = watch("mochibunFrom");
  const mochibunTo = watch("mochibunTo");
  const ownerName = watch("ownerName");
  const isOwnerNameEmpty = !ownerName.trim();
  const pictureTypeZenbujiko = watch("pictureTypeZenbujiko");
  const pictureTypeShoyushajiko = watch("pictureTypeShoyushajiko");

  // 空き家、持ち分以上以下に入力があったら、500件までしか取得しない文章を表示。
  // watch済みの値から導出できるため、state/useEffectを介さずレンダー中に計算する
  const showLimitSentence =
    isEmptyHouse ||
    mochibunFrom.length > 0 ||
    mochibunTo.length > 0 ||
    isSoleOwnership;

  const onSubmit = (data: SearchConditionFormSchema): void => {
    // isSoleOwnershipはUI専用のフィールドなので除外
    const { isSoleOwnership, ...searchData } = data;

    // 単独所有がチェックされている場合は、共有名義人数を1に設定
    if (isSoleOwnership) {
      searchData.mochibunFrom = "1";
      searchData.mochibunTo = "1";
    }
    handleSearch(searchData);
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <SectionTitle>検索</SectionTitle>
      </Box>

      <Box sx={{ mx: 2, mb: 2 }}>
        <Divider />
      </Box>

      <form
        onSubmit={
          handleSubmit(onSubmit) as React.FormEventHandler<HTMLFormElement>
        }
      >
        {/* 都道府県 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <SizingWrapper>
            <Label>都道府県</Label>
            <Controller
              name="prefectures"
              control={control}
              render={({ field, formState: { errors } }) => (
                <div>
                  <FormControl fullWidth>
                    <InputLabel
                      id="prefecture-multiple-checkbox-label"
                      error={!!errors.prefectures}
                    >
                      都道府県
                    </InputLabel>
                    <Select
                      {...field}
                      id="prefecture-multiple-checkbox"
                      error={!!errors.prefectures}
                      input={
                        <OutlinedInput
                          label="都道府県"
                          id="prefecture-multiple-chip"
                        />
                      }
                      labelId="prefecture-multiple-checkbox-label"
                      multiple
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const selectPrefecture = prefectures.find(
                              (prefecture) => prefecture.prefCode === value
                            );

                            return (
                              <Chip
                                key={selectPrefecture?.prefCode}
                                label={selectPrefecture?.name}
                                sx={{ cursor: "pointer" }}
                              />
                            );
                          })}
                        </Box>
                      )}
                      endAdornment={
                        field.value.length > 0 ? (
                          <IconButton
                            sx={{
                              marginRight: "10px",
                            }}
                            onClick={() => {
                              reset({
                                prefectures: [],
                                location: getValues("location"),
                                pictureTypeShoyushajiko: getValues(
                                  "pictureTypeShoyushajiko"
                                ),
                                pictureTypeZenbujiko: getValues(
                                  "pictureTypeZenbujiko"
                                ),
                                isEmptyHouse: getValues("isEmptyHouse"),
                                ownerName: getValues("ownerName"),
                                ownerNameSelectType: getValues(
                                  "ownerNameSelectType"
                                ),
                                ownerAddress: getValues("ownerAddress"),
                                isSoleOwnership: getValues("isSoleOwnership"),
                                mochibunFrom: getValues("mochibunFrom"),
                                mochibunTo: getValues("mochibunTo"),
                                ownerPropertyCountFrom: getValues(
                                  "ownerPropertyCountFrom"
                                ),
                                ownerPropertyCountTo: getValues(
                                  "ownerPropertyCountTo"
                                ),
                                isCurrentOwnerOnly:
                                  getValues("isCurrentOwnerOnly"),
                                isLatestOwnerOnly:
                                  getValues("isLatestOwnerOnly"),
                              });
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        ) : null
                      }
                    >
                      {prefectures.map((prefecture) => (
                        <MenuItem
                          key={prefecture.prefCode}
                          value={prefecture.prefCode}
                        >
                          <Checkbox
                            checked={field.value.includes(prefecture.prefCode)}
                          />
                          <ListItemText primary={prefecture.name} />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error>
                      {errors?.prefectures?.message}
                    </FormHelperText>
                  </FormControl>
                </div>
              )}
            />
          </SizingWrapper>
        </Box>

        {/* 所在 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Label>所在</Label>
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="location"
                    label="所在"
                    type="text"
                    error={!!errors.location}
                    helperText={errors.location ? errors.location.message : ""}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 所有者名・所有者住所 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Label>不動産オーナー名</Label>
              <Controller
                name="ownerName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      if (!e.target.value.trim()) {
                        setValue("ownerNameSelectType", undefined);
                      } else if (getValues("ownerNameSelectType") == null) {
                        setValue("ownerNameSelectType", "startswith");
                      }
                    }}
                    fullWidth
                    id="ownerName"
                    label="不動産オーナー"
                    type="text"
                    error={!!errors.ownerName}
                    helperText={
                      errors.ownerName
                        ? errors.ownerName.message
                        : "フルネームを入力する場合は、姓名の間に空白を入れないでください。"
                    }
                  />
                )}
              />
              {isOwnerNameEmpty && (
                <AlertTitle sx={alertTitleSx}>
                  ・オーナー名が未入力の場合は最大で500件検索します。
                </AlertTitle>
              )}
            </Grid>
            <Grid item xs={6}>
              <Label>不動産オーナー住所</Label>
              <Controller
                name="ownerAddress"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="ownerAddress"
                    label="不動産オーナー住所"
                    type="text"
                    error={!!errors.ownerAddress}
                    helperText={
                      errors.ownerAddress ? errors.ownerAddress.message : ""
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 不動産オーナー検索種別 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <CheckboxGroup label="不動産オーナー検索種別">
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <Controller
                name="ownerNameSelectType"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} value={field.value ?? ""} row>
                    <FormControlLabel
                      value="startswith"
                      control={
                        <Radio size="small" disabled={isOwnerNameEmpty} />
                      }
                      label="前方一致"
                    />
                    <FormControlLabel
                      value="endswith"
                      control={
                        <Radio size="small" disabled={isOwnerNameEmpty} />
                      }
                      label="後方一致"
                    />
                    <FormControlLabel
                      value="contains"
                      control={
                        <Radio size="small" disabled={isOwnerNameEmpty} />
                      }
                      label="部分一致"
                    />
                    <FormControlLabel
                      value="exact"
                      control={
                        <Radio size="small" disabled={isOwnerNameEmpty} />
                      }
                      label="完全一致"
                    />
                  </RadioGroup>
                )}
              />
            </Box>
            {(() => {
              const ownerNameSelectTypeError =
                errors.ownerNameSelectType?.message;
              return (
                ownerNameSelectTypeError && (
                  <FormHelperText error>
                    {ownerNameSelectTypeError}
                  </FormHelperText>
                )
              );
            })()}
          </CheckboxGroup>
        </Box>

        {/* 不動産オーナーの保有物件数 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <Label sx={{ mb: 2 }}>不動産オーナーの保有物件数</Label>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm="auto">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Controller
                  name="ownerPropertyCountFrom"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="ownerPropertyCountFrom"
                      placeholder="下限なし"
                      type="number"
                      onChange={(e) => {
                        field.onChange(e);
                        if (getValues("ownerPropertyCountTo") !== "") {
                          void trigger("ownerPropertyCountTo");
                        }
                      }}
                      error={!!errors.ownerPropertyCountFrom}
                      helperText={errors.ownerPropertyCountFrom?.message ?? ""}
                      onKeyDown={preventNonNumericValueWhenKeyDown}
                      onPaste={preventNonNumericValueWhenPaste}
                      inputProps={{ min: 1 }}
                      sx={{ width: "150px" }}
                    />
                  )}
                />
                <Typography component="span">筆以上</Typography>
              </Box>
            </Grid>
            <Grid item xs="auto">
              <Typography component="span">～</Typography>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Controller
                  name="ownerPropertyCountTo"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="ownerPropertyCountTo"
                      placeholder="上限なし"
                      type="number"
                      onChange={(e) => {
                        field.onChange(e);
                        if (getValues("ownerPropertyCountFrom") !== "") {
                          void trigger("ownerPropertyCountFrom");
                        }
                      }}
                      error={!!errors.ownerPropertyCountTo}
                      helperText={errors.ownerPropertyCountTo?.message ?? ""}
                      onKeyDown={preventNonNumericValueWhenKeyDown}
                      onPaste={preventNonNumericValueWhenPaste}
                      inputProps={{ min: 1 }}
                      sx={{ width: "150px" }}
                    />
                  )}
                />
                <Typography component="span">筆以下</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* 事項種別 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <CheckboxGroup label="事項種別" required={true}>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <FormControlLabel
                control={
                  <Controller
                    name="pictureTypeZenbujiko"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        name="pictureTypeZenbujiko"
                        checked={field.value}
                        onChange={(event) => {
                          field.onChange(event.target.checked);
                        }}
                      />
                    )}
                  />
                }
                label="全部事項"
              />
              <FormControlLabel
                control={
                  <Controller
                    name="pictureTypeShoyushajiko"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        name="pictureTypeShoyushajiko"
                        checked={field.value}
                        onChange={(event) => {
                          field.onChange(event.target.checked);
                        }}
                      />
                    )}
                  />
                }
                label="所有者事項"
              />
            </Box>
            {(() => {
              const pictureTypeError =
                errors.pictureTypeShoyushajiko?.message ??
                errors.pictureTypeZenbujiko?.message;
              return pictureTypeError &&
                !pictureTypeZenbujiko &&
                !pictureTypeShoyushajiko ? (
                <FormHelperText error>{pictureTypeError}</FormHelperText>
              ) : null;
            })()}
          </CheckboxGroup>
        </Box>

        {/* 空き家 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <CheckboxGroup
            label={
              <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                <Typography
                  variant="body1"
                  component="span"
                  sx={{ fontWeight: "bold" }}
                >
                  空き家または賃貸不動産
                </Typography>
                <Tooltip
                  title={
                    <>
                      不動産所在と不動産オーナー住所が異なるものを
                      <br />
                      空き家または賃貸不動産と定義
                    </>
                  }
                  placement="bottom"
                >
                  <HelpIcon sx={{ ml: 1, color: "action.active" }} />
                </Tooltip>
              </Box>
            }
          >
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <FormControlLabel
                control={
                  <Controller
                    name="isEmptyHouse"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        name="isEmptyHouse"
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          field.onChange(event.target.checked);
                        }}
                      />
                    )}
                  />
                }
                label="空き家または賃貸不動産のみを検索する"
              />
            </Box>
          </CheckboxGroup>
        </Box>

        {/* 共有名義人数 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <Label sx={{ mb: 2 }}>
            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
              <Typography
                variant="body1"
                component="span"
                sx={{ fontWeight: "bold" }}
              >
                共有名義人数
              </Typography>
              <Tooltip
                title="不動産を共有名義で所有している人数"
                placement="bottom"
              >
                <HelpIcon
                  aria-label="共有名義人数の説明"
                  sx={{ ml: 1, color: "action.active" }}
                />
              </Tooltip>
            </Box>
          </Label>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm="auto">
              <FormControlLabel
                control={
                  <Controller
                    name="isSoleOwnership"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        name="isSoleOwnership"
                        checked={field.value}
                        onChange={(event) => {
                          field.onChange(event.target.checked);
                          if (event.target.checked) {
                            // チェックされたらテキストボックスを空にする
                            setValue("mochibunFrom", "");
                            setValue("mochibunTo", "");
                          }
                        }}
                      />
                    )}
                  />
                }
                label="単独所有"
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Controller
                  name="mochibunFrom"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="mochibunFrom"
                      placeholder="下限なし"
                      type="number"
                      onChange={(e) => {
                        field.onChange(e);
                        // 上限のフィールドもバリデーションを再実行
                        if (mochibunTo !== "") {
                          void trigger("mochibunTo");
                        }
                      }}
                      error={!!errors.mochibunFrom}
                      helperText={errors.mochibunFrom?.message ?? ""}
                      onKeyDown={preventNonNumericValueWhenKeyDown}
                      onPaste={preventNonNumericValueWhenPaste}
                      inputProps={{ min: 2 }}
                      disabled={isSoleOwnership}
                      sx={{ width: "150px" }}
                    />
                  )}
                />
                <Typography component="span">人以上</Typography>
              </Box>
            </Grid>
            <Grid item xs="auto">
              <Typography component="span">～</Typography>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Controller
                  name="mochibunTo"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="mochibunTo"
                      placeholder="上限なし"
                      type="number"
                      onChange={(e) => {
                        field.onChange(e);
                        // 下限のフィールドもバリデーションを再実行
                        if (mochibunFrom !== "") {
                          void trigger("mochibunFrom");
                        }
                      }}
                      error={!!errors.mochibunTo}
                      helperText={errors.mochibunTo?.message ?? ""}
                      onKeyDown={preventNonNumericValueWhenKeyDown}
                      onPaste={preventNonNumericValueWhenPaste}
                      inputProps={{ min: 2 }}
                      disabled={isSoleOwnership}
                      sx={{ width: "150px" }}
                    />
                  )}
                />
                <Typography component="span">人以下</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* 現在所有者 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <CheckboxGroup label="現在所有者">
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <FormControlLabel
                control={
                  <Controller
                    name="isCurrentOwnerOnly"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        name="isCurrentOwnerOnly"
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          field.onChange(event.target.checked);
                        }}
                      />
                    )}
                  />
                }
                label="現在の所有者のみを検索する"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
              全部事項の甲区履歴に記載された過去の所有者は検索対象に含まれません
            </Typography>
          </CheckboxGroup>
        </Box>

        {/* 所有者変更の可能性がある登記 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <CheckboxGroup label="所有者変更の可能性がある登記">
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <FormControlLabel
                control={
                  <Controller
                    name="isLatestOwnerOnly"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        name="isLatestOwnerOnly"
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          field.onChange(event.target.checked);
                        }}
                      />
                    )}
                  />
                }
                label="「所有者変更の可能性」の注記が付いた登記を除外する"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
              保有している登記より新しい登記で所有者が変わっている可能性がある場合、検索結果に注記が表示されます
            </Typography>
          </CheckboxGroup>
        </Box>

        {/* 空き家または持ち分が入力された場合に表示する文言 */}
        {showLimitSentence ? (
          <Box sx={{ mx: 2, mb: 2 }}>
            <AlertTitle sx={alertTitleSx}>
              ・空き家または共有名義人数を入力した場合は最大で500件検索します。
            </AlertTitle>
          </Box>
        ) : null}

        {/* ボタン */}
        <Box
          sx={{
            mr: 2,
            mb: 3,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isLoading}
            loadingIndicator="検索中..."
          >
            この条件で検索
          </LoadingButton>
        </Box>
      </form>
    </>
  );
};
